import { useCallback, useEffect, useRef } from "react";

const MAX_STACK_SIZE = 50;

/**
 * Undo stack that wraps multiple state setters.
 *
 * Usage:
 *   const { push, undo } = useUndoStack();
 *
 *   // Before mutating state, snapshot the current values:
 *   push({ localConfigs });
 *   setLocalConfigs(next);
 *
 *   // Ctrl+Z automatically calls undo, which restores the latest snapshot.
 *
 * `restorers` is a stable map of state-key → setter function.
 */
export function useUndoStack(
  restorers: Record<string, (value: any) => void>,
) {
  const stackRef = useRef<Record<string, unknown>[]>([]);
  const restorersRef = useRef(restorers);
  restorersRef.current = restorers;

  const push = useCallback((snapshot: Record<string, unknown>) => {
    stackRef.current.push(snapshot);
    if (stackRef.current.length > MAX_STACK_SIZE) {
      stackRef.current.shift();
    }
  }, []);

  const undo = useCallback(() => {
    const snapshot = stackRef.current.pop();
    if (!snapshot) return false;
    for (const [key, value] of Object.entries(snapshot)) {
      const setter = restorersRef.current[key];
      if (setter) setter(value);
    }
    return true;
  }, []);

  const clear = useCallback(() => {
    stackRef.current = [];
  }, []);

  // Ctrl+Z / Cmd+Z listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo]);

  return { push, undo, clear };
}
